import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronDown,
  Plus,
  Check,
  Building2,
  Loader2,
  FolderOpen,
  Sparkles,
  Settings as SettingsIcon
} from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { Project } from '../../services/projectService';
import UsageBadge from '../ui/UsageBadge';

interface ProjectSwitcherProps {
  collapsed?: boolean;
}

const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentProject, projects, isLoading, switchProject, createProject } = useProject();

  // Employees with viewer/editor role can't create projects
  const isEmployee = !!user?.ownerUserId;
  const canCreateProject = !isEmployee || user?.employeeRole === 'admin';
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewProjectName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchProject = async (project: Project) => {
    try {
      await switchProject(project.id);
      setIsOpen(false);
    } catch (error) {
      console.error('Error switching project:', error);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    setIsCreating(true);
    try {
      const newProject = await createProject(newProjectName.trim());
      // Project created successfully - redirect to onboarding
      // localStorage already has the new project ID (set by createProject in context)
      window.location.href = '/onboarding?new=true';
    } catch (error: any) {
      console.error('Error creating project:', error);
      setIsCreating(false);
      const msg = error?.response?.data?.message || error?.message || t('projects.createError', 'Erro ao criar projeto');
      toast.error(msg);
    }
  };

  const activeProjects = projects.filter(p => p.is_active);

  if (isLoading) {
    return (
      <div className="py-2">
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-800/50 rounded-xl animate-pulse">
          <div className="w-9 h-9 bg-gray-700 rounded-lg" />
          {!collapsed && <div className="h-4 w-24 bg-gray-700 rounded" />}
        </div>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="py-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-center p-2 rounded-xl hover:bg-white/5 transition-colors"
          title={currentProject?.name || t('projects.selectProject', 'Selecionar Projeto')}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-lg"
            style={{ backgroundColor: currentProject?.color || '#6366f1' }}
          >
            {currentProject?.name?.charAt(0).toUpperCase() || <FolderOpen className="w-4 h-4" />}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="py-2 relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-800/50 hover:bg-gray-800 transition-all duration-200 border border-gray-700/50 group"
      >
        <div className="relative">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-white/10"
            style={{ backgroundColor: currentProject?.color || '#6366f1' }}
          >
            {currentProject?.name?.charAt(0).toUpperCase() || <Building2 className="w-4 h-4" />}
          </div>
          {currentProject && (
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
          )}
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {currentProject?.name || t('projects.noProject', 'Nenhum projeto')}
          </p>
          {currentProject?.businessProfile?.business_name ? (
            <p className="text-xs text-gray-500 truncate">
              {currentProject.businessProfile.business_name}
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              {t('projects.workspace', 'Workspace')}
            </p>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 group-hover:text-gray-400 transition-all duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-700/50">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              {t('projects.yourProjects', 'Seus Projetos')}
            </p>
          </div>

          {/* Projects List */}
          <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-dark py-2">
            {activeProjects.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <FolderOpen className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  {t('projects.noProjects', 'Nenhum projeto encontrado')}
                </p>
              </div>
            ) : (
              activeProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleSwitchProject(project)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 transition-all duration-200 ${
                    currentProject?.id === project.id
                      ? 'bg-primary-500/10 border-l-2 border-primary-500'
                      : 'hover:bg-white/5 border-l-2 border-transparent'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 shadow-md"
                    style={{ backgroundColor: project.color || '#6366f1' }}
                  >
                    {project.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      currentProject?.id === project.id ? 'text-primary-400' : 'text-gray-200'
                    }`}>
                      {project.name}
                    </p>
                  </div>
                  {currentProject?.id === project.id && (
                    <div className="w-5 h-5 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary-400" />
                    </div>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700/50" />

          {canCreateProject && (
            <div className="px-3 pt-3 flex justify-start">
              <UsageBadge resource="projects" compact />
            </div>
          )}

          {/* Create Project Section - only for owners and admin employees */}
          {canCreateProject && (
            <>
              {showCreateForm ? (
                <div className="p-3">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                    placeholder={t('projects.projectName', 'Nome do projeto')}
                    className="w-full px-3 py-2.5 text-sm border border-gray-600 rounded-lg bg-gray-700/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleCreateProject}
                      disabled={!newProjectName.trim() || isCreating}
                      className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg hover:from-primary-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                    >
                      {isCreating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          {t('common.create', 'Criar')}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewProjectName('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors"
                    >
                      {t('common.cancel', 'Cancelar')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-primary-400 hover:bg-white/5 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
                    <Plus className="w-4 h-4" />
                  </div>
                  {t('projects.newProject', 'Novo Projeto')}
                </button>
              )}
            </>
          )}

          {/* Gerenciar projetos — link claro para Configurações (excluir/editar projeto) */}
          <div className="border-t border-gray-700/50" />
          <button
            onClick={() => { setIsOpen(false); navigate('/settings'); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-300 hover:bg-white/5 transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-500/10 flex items-center justify-center group-hover:bg-gray-500/20 transition-colors">
              <SettingsIcon className="w-4 h-4" />
            </div>
            {t('projects.manageProjects', 'Gerenciar projetos')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;
